import { HttpClient, HttpParams, type HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, throwError } from 'rxjs';

import type { AssignmentUserRequestBody } from '../../accounts/models/account.models';
import { APP_ENV } from '../../../core/tokens/app-environment.token';
import { catalogUserOId } from '../../../shared/models/momentum-user.models';
import type { MomentumUser } from '../../../shared/models/momentum-user.models';
import type {
  OpportunityListQueryParams,
  OpportunityListResponseDto,
  OpportunityStatsResponse,
} from '../models/opportunity-list.models';
import type {
  CreateWorkspaceRequestBody,
  CreateWorkspaceWithOpportunityResponseDto,
  OpportunityResponseDto,
  OpportunityUpdateRequestBody,
} from '../opportunity.mapper';
import type { CrmOpportunitySearchResultDto } from '../models/crm-opportunity.models';

export type {
  CreateWorkspaceRequestBody,
  CreateWorkspaceWithOpportunityResponseDto,
  OpportunityResponseDto,
  OpportunityUpdateRequestBody,
} from '../opportunity.mapper';

export type {
  OpportunityListQueryParams,
  OpportunityListResponseDto,
  OpportunityStatsResponse,
} from '../models/opportunity-list.models';

/** HTTP only — opportunity list, read, create, and update. */
@Injectable({ providedIn: 'root' })
export class OpportunityApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(APP_ENV);

  private opportunityUrl(opportunityId: string): string {
    const encoded = encodeURIComponent(opportunityId);
    return `${this.env.apiBaseUrl}/opportunities/${encoded}`;
  }

  listOpportunities(
    query: OpportunityListQueryParams = {},
  ): Observable<OpportunityListResponseDto> {
    return this.http.get<OpportunityListResponseDto>(`${this.env.apiBaseUrl}/opportunities`, {
      params: this.buildListParams(query),
    });
  }

  getStats(): Observable<OpportunityStatsResponse> {
    return this.http.get<OpportunityStatsResponse>(`${this.env.apiBaseUrl}/opportunities/stats`);
  }

  getOpportunity(opportunityId: string): Observable<OpportunityResponseDto> {
    return this.http.get<OpportunityResponseDto>(this.opportunityUrl(opportunityId));
  }

  exportMarkdown(opportunityId: string, includeChatHistory: boolean) {
    const params = new HttpParams().set('includeChatHistory', String(includeChatHistory));
    return this.http.get(`${this.opportunityUrl(opportunityId)}/export`, {
      params,
      observe: 'response',
      responseType: 'blob',
    }) as Observable<HttpResponse<Blob>>;
  }

  createWorkspace(
    body: CreateWorkspaceRequestBody = {},
  ): Observable<CreateWorkspaceWithOpportunityResponseDto> {
    return this.http.post<CreateWorkspaceWithOpportunityResponseDto>(
      `${this.env.apiBaseUrl}/opportunities/workspaces`,
      body,
    );
  }

  updateOpportunity(
    opportunityId: string,
    body: OpportunityUpdateRequestBody,
  ): Observable<OpportunityResponseDto> {
    return this.http.put<OpportunityResponseDto>(this.opportunityUrl(opportunityId), body);
  }

  reassignOwner(opportunityId: string, owner: MomentumUser): Observable<OpportunityResponseDto> {
    const userOId = catalogUserOId(owner);
    if (!userOId) {
      return throwError(() => new Error('Opportunity owner must be a directory user.'));
    }
    const body: AssignmentUserRequestBody = { userOId };
    return this.http.post<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/owner`,
      body,
    );
  }

  completeOpportunity(opportunityId: string): Observable<OpportunityResponseDto> {
    return this.http.put<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/complete`,
      {},
    );
  }

  listCrmOpportunities(crmAccountId: string): Observable<CrmOpportunitySearchResultDto[]> {
    const params = new HttpParams().set('crmAccountId', crmAccountId.trim());
    return this.http.get<CrmOpportunitySearchResultDto[]>(
      `${this.env.apiBaseUrl}/opportunities/directory`,
      { params },
    );
  }

  linkCrmOpportunity(
    opportunityId: string,
    crmOpportunityId: string,
  ): Observable<OpportunityResponseDto> {
    return this.http.post<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/crm-link`,
      {
        crmOpportunityId,
      },
    );
  }

  syncCrmLink(opportunityId: string, full = false): Observable<OpportunityResponseDto> {
    let params = new HttpParams();
    if (full) {
      params = params.set('full', 'true');
    }
    return this.http.post<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/crm-sync`,
      {},
      { params },
    );
  }

  syncCNumber(opportunityId: string): Observable<OpportunityResponseDto> {
    return this.http.post<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/c-number-sync`,
      {},
    );
  }

  updateSubmissionDeadline(
    opportunityId: string,
    submissionDeadline: string,
  ): Observable<OpportunityResponseDto> {
    return this.http.put<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/submission-deadline`,
      { submissionDeadline },
    );
  }

  updateOpportunityLinks(
    opportunityId: string,
    miroWebUrl: string,
  ): Observable<OpportunityResponseDto> {
    return this.http.put<OpportunityResponseDto>(`${this.opportunityUrl(opportunityId)}/links`, {
      miroWebUrl,
    });
  }

  startBid(opportunityId: string): Observable<OpportunityResponseDto> {
    return this.http.post<OpportunityResponseDto>(
      `${this.opportunityUrl(opportunityId)}/start-bid`,
      {},
    );
  }

  deleteOpportunity(opportunityId: string, deleteReason: string | null): Observable<void> {
    return this.http.delete<void>(this.opportunityUrl(opportunityId), {
      body: { deleteReason },
    });
  }

  private buildListParams(query: OpportunityListQueryParams): HttpParams {
    let params = new HttpParams();

    if (query.pageSize != null) {
      params = params.set('pageSize', String(query.pageSize));
    }
    if (query.continuationToken) {
      params = params.set('continuationToken', query.continuationToken);
    }
    if (query.sortBy) {
      params = params.set('sortBy', query.sortBy);
    }
    if (query.sortAsc != null) {
      params = params.set('sortAsc', String(query.sortAsc));
    }

    return params;
  }
}
